import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, InputInfoMsg } from "components/common/input/Input";
import { Button } from "wiz-front-shared/components/button/Button";
import { CheckBox } from "wiz-front-shared/components/checkBox/CheckBox";
import { RadioGroup, RadioItem } from "components/common/radioGroup/RadioGroup";
import { DateWheelPicker, DateWheelPickerRef } from "components/common/wheelPicker/WheelPicker";
import { isUnder14FromDateString } from "utils/checkDate";
import "./Identity.css";

export type Gender = "MALE" | "FEMALE" | "OTHER" | "";

export interface IdentityBirthGenderResult {
    birth: string;       // YYYYMMDD
    gender: Gender;
    parentConsent: boolean;  // enableParentConsent=true 이고 14세 미만 일 때 true
}

interface Props {
    initialBirth?: string;
    initialGender?: Gender;
    /** 14세 미만 보호자 동의 초기값 (device back 으로 재진입 시 체크 상태 복원용) */
    initialParentConsent?: boolean;

    /** 14세 미만 보호자 동의 사용 여부 (SignUp 전용 — 법적 요건). 기본 false. */
    enableParentConsent?: boolean;

    /** "선택안함(OTHER)" 노출 여부. 기본 true.
     *   KR 다날 본인인증 경로에선 genderDigit(주민번호 뒷자리 첫 숫자) 계산이 필요해 OTHER 선택 시
     *   다음 단계에서 호출 실패 → KR 호출 측에서 false 전달. */
    enableOtherGender?: boolean;

    /** 상단 타이틀 i18n key (옵션) — 미성년자/보호자 회차 구분 안내용 */
    titleI18nKey?: string;
    /** 상단 안내 i18n key (옵션) */
    infoI18nKey?: string;

    /** 다음 버튼 콜백 — 최종 값을 직접 전달 (race-free) */
    onNext: (result: IdentityBirthGenderResult) => void;
}

/**
 * 본인인증 - 생년월일 + 성별 입력
 *
 * State 는 form 내부에서만 관리. 부모는 `onNext(result)` 로 최종 값 수령.
 * 14세 미만 보호자 동의 분기는 `enableParentConsent` 옵션으로 활성화 (SignUp 전용).
 */
export const IdentityBirthGenderForm = (props: Props) => {
    const { t } = useTranslation();

    const {
        initialBirth,
        initialGender,
        initialParentConsent = false,
        enableParentConsent = false,
        enableOtherGender = true,
        titleI18nKey,
        infoI18nKey,
        onNext,
    } = props;

    // 내부 표시용: YYYY-MM-DD
    const [birthDate, setBirthDate] = useState(() => {
        if (initialBirth && initialBirth.length === 8) {
            return `${initialBirth.slice(0, 4)}-${initialBirth.slice(4, 6)}-${initialBirth.slice(6, 8)}`;
        }
        return "";
    });
    const [gender, setGender] = useState<Gender>(initialGender || "");
    const [parentConsent, setParentConsent] = useState(initialParentConsent);

    const [birthInfoMsg, setBirthInfoMsg] = useState<InputInfoMsg>();
    const birthDateRef = useRef<DateWheelPickerRef>(null);

    const genderItems: RadioItem[] = [
        { label: t("signUp.step2.male"), value: "MALE" },
        { label: t("signUp.step2.female"), value: "FEMALE" },
        ...(enableOtherGender ? [{ label: t("signUp.step2.other"), value: "OTHER" }] : []),
    ];

    // KR 컨텍스트 진입인데 initial 이 OTHER (SNS 데이터 등) 인 경우 → 재선택 유도.
    //   라디오 옵션이 사라져 화면상 선택 표시도 안 되고, 그대로 두면 다날 호출 시 genderDigit 계산 실패.
    useEffect(() => {
        if (!enableOtherGender && gender === "OTHER") {
            setGender("");
        }
    }, [enableOtherGender, gender]);

    const [isUnder14, setIsUnder14] = useState(false);
    const [isBirthValid, setIsBirthValid] = useState(false);
    const [isGenderValid, setIsGenderValid] = useState(false);
    const [isBtnDisabled, setIsBtnDisabled] = useState(true);

    // birthDate 변경 → 검증 + 14세 미만 판단
    useEffect(() => {
        if (!birthDate) {
            setBirthInfoMsg(undefined);
            setIsBirthValid(false);
            setIsUnder14(false);
            return;
        }
        setBirthInfoMsg(undefined);
        setIsBirthValid(true);

        const under14 = isUnder14FromDateString(birthDate);
        setIsUnder14(under14);
        if (!under14) {
            setParentConsent(false);
        }
    }, [birthDate]);

    // 성별 검증
    useEffect(() => {
        setIsGenderValid(!!gender);
    }, [gender]);

    // 다음 버튼 활성화
    useEffect(() => {
        if (enableParentConsent && isUnder14) {
            setIsBtnDisabled(!isBirthValid || !isGenderValid || !parentConsent);
        } else {
            setIsBtnDisabled(!isBirthValid || !isGenderValid);
        }
    }, [enableParentConsent, isUnder14, isBirthValid, isGenderValid, parentConsent]);

    const handleNext = () => {
        if (isBtnDisabled) return;
        onNext({
            birth: birthDate.replace(/-/g, ""),
            gender,
            parentConsent: enableParentConsent && isUnder14 ? parentConsent : false,
        });
    };

    return (
        <div className="identity-form">
            {titleI18nKey && <span className="identity-title">{t(titleI18nKey)}</span>}
            {infoI18nKey && <span className="identity-info">{t(infoI18nKey)}</span>}

            {/* 성별 — 생년월일 입력 후 노출 (sideStep 패턴) */}
            <div
                className={`identity-hide-container ${isBirthValid && (!isUnder14 || parentConsent || !enableParentConsent) ? "" : "hide"} `}
            >
                <RadioGroup
                    name="identity-gender"
                    items={genderItems}
                    value={gender}
                    onChange={(v) => setGender(v as Gender)}
                    title={t("signUp.step2.gender")}
                    isRequired
                />
            </div>

            {/* 생년월일 */}
            <Input
                id="identity-birthDate"
                inputVal={birthDate ? birthDate.replace(/-/g, ".") : ""}
                title={t("signUp.step2.birthDate")}
                infoMsg={birthInfoMsg}
                isRequired
                readOnly
                placeholder={t("signUp.step2.birthDatePlaceHolder")}
                onClick={() => birthDateRef.current?.open()}
                showClearButton={false}
            />
            <DateWheelPicker
                ref={birthDateRef}
                value={birthDate}
                onChange={setBirthDate}
            />

            {/* 14세 미만 보호자 동의 (SignUp 전용) */}
            {enableParentConsent && (
                <div className={`identity-parent-consent-area ${isUnder14 ? "show" : ""}`}>
                    <CheckBox
                        title={t("signUp.step2.parenConsentWarning")}
                        id="identity-parent-consent"
                        checked={parentConsent}
                        setChecked={setParentConsent}
                        required
                    />
                    <span className="identity-parent-consent-info">{t("signUp.step2.info")}</span>
                </div>
            )}

            <div className="identity-bottom-btn">
                <Button
                    text={t("signUp.btnNext")}
                    onPress={handleNext}
                    disabled={isBtnDisabled}
                />
            </div>
        </div>
    );
};
